<template>
  <div class="enhanced-image-viewer">
    <!-- 图片容器 -->
    <div class="image-container" @click="handleContainerClick">
      <img
        ref="imageRef"
        :src="src"
        :alt="alt"
        class="preview-image"
        :style="imageStyle"
        @load="onImageLoaded"
        @error="onImageError"
      />
    </div>

    <!-- 加载指示器 -->
    <div class="loading-indicator" v-if="isLoading">
      <el-icon class="loading-spinner"><Loading /></el-icon>
      <span>图片加载中...</span>
    </div>

    <!-- 错误提示 -->
    <div class="error-message" v-if="hasError">
      <el-icon class="error-icon"><CircleClose /></el-icon>
      <p>{{ errorMessage }}</p>
      <el-button type="primary" @click="retryLoading">重新加载</el-button>
    </div>

    <!-- 控制栏 -->
    <div class="control-bar" v-if="!hasError && !isLoading">
      <!-- 缩放控制 -->
      <div class="zoom-controls">
        <button 
          class="control-btn" 
          @click="zoomOut" 
          :disabled="scale <= minScale"
          title="缩小"
        >
          <el-icon><ZoomOut /></el-icon>
        </button>
        <button class="control-btn" @click="resetZoom" title="重置缩放">
          <el-icon><Refresh /></el-icon>
        </button>
        <button 
          class="control-btn" 
          @click="zoomIn" 
          :disabled="scale >= maxScale"
          title="放大"
        >
          <el-icon><ZoomIn /></el-icon>
        </button>
        <span class="zoom-level">{{ Math.round(scale * 100) }}%</span>
      </div>

      <!-- 旋转控制 -->
      <div class="rotate-controls">
        <button class="control-btn" @click="rotateLeft" title="向左旋转">
          <el-icon><RefreshLeft /></el-icon>
        </button>
        <button class="control-btn" @click="rotateRight" title="向右旋转">
          <el-icon><RefreshRight /></el-icon>
        </button>
      </div>

      <!-- 全屏控制 -->
      <div class="fullscreen-controls">
        <button class="control-btn" @click="toggleFitToScreen" :class="{ active: fitToScreen }" title="适应屏幕">
          <el-icon><ZoomIn /></el-icon>
        </button>
        <button class="control-btn" @click="toggleFullscreen" title="全屏查看">
          <el-icon v-if="!isFullscreen"><FullScreen /></el-icon>
          <el-icon v-else><CloseFullScreen /></el-icon>
        </button>
      </div>
    </div>

    <!-- 全屏模式提示 -->
    <div class="fullscreen-tip" v-if="isFullscreen">
      <p>按 Esc 键退出全屏</p>
    </div>
  </div>
</template>

<script>
import { ElIcon, ElButton } from 'element-plus'
import { 
  ZoomIn, ZoomOut, Refresh, RefreshLeft, RefreshRight, 
  FullScreen, CloseFullScreen, Loading, CircleClose 
} from '@element-plus/icons-vue'

export default {
  name: 'EnhancedImageViewer',
  components: {
    ElIcon,
    ElButton,
    ZoomIn,
    ZoomOut,
    Refresh,
    RefreshLeft,
    RefreshRight,
    FullScreen,
    CloseFullScreen,
    Loading,
    CircleClose
  },
  props: {
    src: {
      type: String,
      required: true
    },
    alt: {
      type: String,
      default: '预览图片'
    },
    initialScale: {
      type: Number,
      default: 1.0
    },
    zoomStep: {
      type: Number,
      default: 0.1
    },
    minScale: {
      type: Number,
      default: 0.1
    },
    maxScale: {
      type: Number,
      default: 5.0
    },
    autoFitToScreen: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      scale: this.initialScale,
      rotation: 0,
      isLoading: true,
      hasError: false,
      errorMessage: '',
      isFullscreen: false,
      fitToScreen: this.autoFitToScreen,
      imageNaturalWidth: 0,
      imageNaturalHeight: 0
    }
  },
  computed: {
    // 计算图片样式
    imageStyle() {
      let computedScale = this.scale
      
      // 如果启用了适应屏幕，根据容器大小调整缩放
      if (this.fitToScreen && this.imageNaturalWidth > 0 && this.imageNaturalHeight > 0) {
        const container = this.$el.querySelector('.image-container')
        if (container) {
          const containerWidth = container.clientWidth - 40 // 减去边距
          const containerHeight = container.clientHeight - 40 // 减去边距
          
          // 计算适应屏幕的缩放比例
          const widthRatio = containerWidth / this.imageNaturalWidth
          const heightRatio = containerHeight / this.imageNaturalHeight
          computedScale = Math.min(widthRatio, heightRatio)
        }
      }
      
      return {
        transform: `scale(${computedScale}) rotate(${this.rotation}deg)`,
        transition: 'transform 0.2s ease'
      }
    }
  },
  mounted() {
    this.setupEventListeners()
  },
  beforeUnmount() {
    this.cleanupEventListeners()
  },
  methods: {
    setupEventListeners() {
      // 监听全屏变化事件
      document.addEventListener('fullscreenchange', this.handleFullscreenChange)
      document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange)
      document.addEventListener('mozfullscreenchange', this.handleFullscreenChange)
      document.addEventListener('MSFullscreenChange', this.handleFullscreenChange)
      
      // 监听键盘事件
      document.addEventListener('keydown', this.handleKeydown)
      
      // 监听窗口大小变化，用于适应屏幕功能
      window.addEventListener('resize', this.handleResize)
    },
    
    cleanupEventListeners() {
      document.removeEventListener('fullscreenchange', this.handleFullscreenChange)
      document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange)
      document.removeEventListener('mozfullscreenchange', this.handleFullscreenChange)
      document.removeEventListener('MSFullscreenChange', this.handleFullscreenChange)
      document.removeEventListener('keydown', this.handleKeydown)
      window.removeEventListener('resize', this.handleResize)
    },
    
    onImageLoaded(event) {
      this.isLoading = false
      this.hasError = false
      
      // 保存图片原始尺寸
      this.imageNaturalWidth = event.target.naturalWidth
      this.imageNaturalHeight = event.target.naturalHeight
    },
    
    onImageError() {
      this.isLoading = false
      this.hasError = true
      this.errorMessage = '图片加载失败，请检查网络连接或图片是否存在'
    },
    
    retryLoading() {
      this.hasError = false
      this.isLoading = true
      if (this.$refs.imageRef) {
        this.$refs.imageRef.src = ''
        setTimeout(() => {
          this.$refs.imageRef.src = this.src
        }, 100)
      }
    },
    
    // 放大图片
    zoomIn() {
      if (this.scale < this.maxScale) {
        this.scale = Math.min(this.scale + this.zoomStep, this.maxScale)
        this.fitToScreen = false // 手动缩放时关闭适应屏幕
      }
    },
    
    // 缩小图片
    zoomOut() {
      if (this.scale > this.minScale) {
        this.scale = Math.max(this.scale - this.zoomStep, this.minScale)
        this.fitToScreen = false // 手动缩放时关闭适应屏幕
      }
    },
    
    // 重置缩放
    resetZoom() {
      this.scale = this.initialScale
      this.rotation = 0
      this.fitToScreen = this.autoFitToScreen
    },
    
    // 向左旋转
    rotateLeft() {
      this.rotation = (this.rotation - 90 + 360) % 360
    },
    
    // 向右旋转
    rotateRight() {
      this.rotation = (this.rotation + 90) % 360
    },
    
    // 切换全屏
    toggleFullscreen() {
      const viewer = this.$el
      if (!document.fullscreenElement) {
        // 进入全屏
        if (viewer.requestFullscreen) {
          viewer.requestFullscreen()
        } else if (viewer.webkitRequestFullscreen) {
          viewer.webkitRequestFullscreen()
        } else if (viewer.mozRequestFullScreen) {
          viewer.mozRequestFullScreen()
        } else if (viewer.msRequestFullscreen) {
          viewer.msRequestFullscreen()
        }
      } else {
        // 退出全屏
        if (document.exitFullscreen) {
          document.exitFullscreen()
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen()
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen()
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen()
        }
      }
    },
    
    // 处理全屏变化
    handleFullscreenChange() {
      this.isFullscreen = !!(document.fullscreenElement || 
                             document.webkitFullscreenElement || 
                             document.mozFullScreenElement || 
                             document.msFullscreenElement)
    },
    
    // 切换适应屏幕
    toggleFitToScreen() {
      this.fitToScreen = !this.fitToScreen
      if (this.fitToScreen) {
        // 重置缩放，让适应屏幕生效
        this.scale = this.initialScale
      }
    },
    
    // 处理容器点击
    handleContainerClick(event) {
      // 如果点击的是容器背景（不是图片本身），可以切换适应屏幕
      if (event.target === event.currentTarget) {
        this.toggleFitToScreen()
      }
    },
    
    // 处理键盘事件
    handleKeydown(event) {
      // 只有在全屏模式下才响应键盘事件
      if (!this.isFullscreen) return
      
      switch (event.key) {
        case '+':
        case '=':
          event.preventDefault()
          this.zoomIn()
          break
        case '-':
          event.preventDefault()
          this.zoomOut()
          break
        case '0':
          event.preventDefault()
          this.resetZoom()
          break
        case 'ArrowLeft':
          event.preventDefault()
          this.rotateLeft()
          break
        case 'ArrowRight':
          event.preventDefault()
          this.rotateRight()
          break
        case 'f':
          event.preventDefault()
          this.toggleFitToScreen()
          break
        case 'Escape':
          // 退出全屏由浏览器自动处理
          break
      }
    },
    
    // 处理窗口大小变化
    handleResize() {
      // 窗口大小变化时，如果启用了适应屏幕，图片会自动调整
      // 这里不需要额外操作，computed属性会自动更新
    }
  }
}
</script>

<style scoped>
.enhanced-image-viewer {
  position: relative;
  width: 100%;
  height: 100%;
  background-color: #f0f0f0;
  border-radius: 8px;
  overflow: hidden;
}

.image-container {
  position: relative;
  width: 100%;
  height: calc(100% - 60px); /* 减去控制栏高度 */
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-color: #000;
  cursor: zoom-in;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transition: transform 0.2s ease;
  cursor: grab;
  user-select: none;
}

.preview-image:active {
  cursor: grabbing;
}

.loading-indicator {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: #333;
  background-color: rgba(255, 255, 255, 0.9);
  padding: 20px;
  border-radius: 8px;
  z-index: 10;
}

.loading-spinner {
  font-size: 32px;
  animation: spin 1s linear infinite;
}

.error-message {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 15px;
  color: #333;
  background-color: rgba(255, 255, 255, 0.95);
  padding: 30px;
  border-radius: 8px;
  text-align: center;
  max-width: 90%;
  z-index: 10;
}

.error-icon {
  font-size: 48px;
  color: #f56c6c;
}

.control-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 60px;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  color: #fff;
}

.zoom-controls,
.rotate-controls,
.fullscreen-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.control-btn {
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #fff;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  min-width: 40px;
  height: 40px;
}

.control-btn:hover:not(:disabled) {
  background-color: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.5);
}

.control-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.control-btn.active {
  background-color: #409eff;
  border-color: #409eff;
}

.zoom-level {
  font-size: 14px;
  min-width: 60px;
  text-align: center;
}

.fullscreen-tip {
  position: absolute;
  top: 20px;
  right: 20px;
  background-color: rgba(0, 0, 0, 0.7);
  color: #fff;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 12px;
  animation: fadeInOut 3s ease-in-out;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes fadeInOut {
  0% { opacity: 0; }
  20% { opacity: 1; }
  80% { opacity: 1; }
  100% { opacity: 0; }
}

/* 全屏模式样式 */
:deep(.enhanced-image-viewer:fullscreen) {
  background-color: #000;
  z-index: 9999;
}

:deep(.enhanced-image-viewer:-webkit-full-screen) {
  background-color: #000;
  z-index: 9999;
}

:deep(.enhanced-image-viewer:-moz-full-screen) {
  background-color: #000;
  z-index: 9999;
}

:deep(.enhanced-image-viewer:-ms-fullscreen) {
  background-color: #000;
  z-index: 9999;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .control-bar {
    padding: 0 10px;
  }
  
  .control-btn {
    min-width: 36px;
    height: 36px;
    padding: 6px 8px;
  }
  
  .zoom-level {
    font-size: 12px;
    min-width: 50px;
  }
  
  .rotate-controls {
    display: none;
  }
}
</style>